import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import type { RootState } from "@/store"
import type { OffboardingDto } from "@/modules/offboarding/dtos"
import { apiClient } from "@/lib/api-client"
import { HttpStatusCodes } from "@/lib/http-status-codes"

interface EmployeeOffboardingState {
  data: OffboardingDto | null
  loading: boolean
  initiating: boolean
  notFound: boolean
  error: string | null
}

const initialState: EmployeeOffboardingState = {
  data: null,
  loading: false,
  initiating: false,
  notFound: false,
  error: null,
}

export const fetchEmployeeOffboardingThunk = createAsyncThunk<
  OffboardingDto,
  string,
  { rejectValue: { notFound: boolean; message: string } }
>(
  "employeeOffboarding/fetch",
  async (employeeId, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.get<OffboardingDto>(
        `/EmployeeOffboarding/employee/${employeeId}`,
      )
      return data
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      return rejectWithValue({
        notFound: status === HttpStatusCodes.NOT_FOUND,
        message: status === HttpStatusCodes.NOT_FOUND ? "No offboarding found" : "Failed to fetch offboarding",
      })
    }
  },
)

export const employeeInitiateOffboardingThunk = createAsyncThunk<
  OffboardingDto,
  { employeeId: string; offboardingReason: string; resignationLetter: File },
  { rejectValue: string }
>(
  "employeeOffboarding/initiate",
  async (payload, { rejectWithValue }) => {
    try {
      const formData = new FormData()
      formData.append("employeeId", payload.employeeId)
      formData.append("offboardingReason", payload.offboardingReason)
      formData.append("resignationLetter", payload.resignationLetter)
      const { data } = await apiClient.post<OffboardingDto>(
        "/EmployeeOffboarding/employee-initiate",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      )
      return data
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      return rejectWithValue(msg ?? "Failed to start offboarding")
    }
  },
)

const employeeOffboardingSlice = createSlice({
  name: "employeeOffboarding",
  initialState,
  reducers: {},
  extraReducers: (b) => {
    b
      .addCase(fetchEmployeeOffboardingThunk.pending, (s) => {
        s.loading = true; s.notFound = false; s.error = null
      })
      .addCase(fetchEmployeeOffboardingThunk.fulfilled, (s, a) => {
        s.loading = false; s.data = a.payload
      })
      .addCase(fetchEmployeeOffboardingThunk.rejected, (s, a) => {
        s.loading = false
        s.notFound = a.payload?.notFound ?? false
        s.error = a.payload?.message ?? "Failed"
      })
      .addCase(employeeInitiateOffboardingThunk.pending, (s) => {
        s.initiating = true; s.error = null
      })
      .addCase(employeeInitiateOffboardingThunk.fulfilled, (s, a) => {
        s.initiating = false; s.notFound = false; s.data = a.payload
      })
      .addCase(employeeInitiateOffboardingThunk.rejected, (s, a) => {
        s.initiating = false; s.error = a.payload ?? "Failed"
      })
  },
})

export default employeeOffboardingSlice.reducer

export const selectEmployeeOffboarding = (s: RootState) => s.employeeOffboarding
